/**
 * Base HTTP Error class that extends the native Error class
 * All custom HTTP errors inherit from this class
 */
export class HttpError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly url: string,
    public readonly context: Record<string, any> = {}
  ) {
    super(message);
    this.name = this.constructor.name;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (typeof (Error as any).captureStackTrace === "function") {
      (Error as any).captureStackTrace(this, this.constructor);
    }
  }
}

/**
 * Bad Request Error (400)
 * Thrown when the server cannot process the request due to client error
 */
export class BadRequestError extends HttpError {
  constructor(url: string, errorBody: any, timestamp: string) {
    super(`Bad Request Error: ${url}`, 400, url, {
      errorBody,
      timestamp,
    });
  }
}

/**
 * Unauthorized Error (401)
 * Thrown when authentication is required or has failed
 */
export class UnauthorizedError extends HttpError {
  constructor(url: string, timestamp: string) {
    super(`Authentication Failed: ${url}`, 401, url, {
      message: "User authentication token may be expired or invalid",
      timestamp,
    });
  }
}

/**
 * Forbidden Error (403)
 * Thrown when the user does not have permission to access the resource
 */
export class ForbiddenError extends HttpError {
  constructor(url: string, timestamp: string) {
    super(`Access Forbidden: ${url}`, 403, url, {
      message: "User does not have permission to access this resource",
      timestamp,
    });
  }
}

/**
 * Not Found Error (404)
 * Thrown when the requested resource cannot be found
 */
export class NotFoundError extends HttpError {
  constructor(url: string, timestamp: string) {
    super(`Resource Not Found: ${url}`, 404, url, {
      timestamp,
    });
  }
}

/**
 * Internal Server Error (500)
 * Thrown when the server encounters an unexpected condition
 */
export class InternalServerError extends HttpError {
  constructor(url: string, errorBody: any, timestamp: string) {
    super(`Internal Server Error: ${url}`, 500, url, {
      errorBody,
      timestamp,
    });
  }
}

/**
 * Generic HTTP Error
 * Thrown for any other HTTP error status codes not covered by specific error types
 */
export class GenericHttpError extends HttpError {
  constructor(status: number, url: string, errorBody: any, timestamp: string) {
    super(`HTTP Error ${status}: ${url}`, status, url, {
      errorBody,
      timestamp,
    });
  }
}
